import React from 'react'
import { Stack } from '@hub/stack'
import {
  As,
  HubResponsiveValue,
  HubStyleObject,
  StandardSizes,
  StandardSpacings,
  forwardRef,
} from '@hub/design-system-base'
import type { FlexOptions } from '@chakra-ui/react'

export interface ReelProps {
  as?: As
  minWidth?: HubResponsiveValue<StandardSizes>
  maxWidth?: HubResponsiveValue<StandardSizes>
  gap?: HubResponsiveValue<StandardSpacings>
  className?: string
  snap?: boolean
  sx?: HubStyleObject
  flexBasis?: FlexOptions['basis']
  /**
   * Wrap each child in a Hub <Box>. Can be useful when children styles
   * conflict with the styles a <Stack> needs to inject.
   */
  shouldWrapChildren?: boolean
}

/**
 * NOTE: This is a simplified implementation of `@hub/reel` with very specific
 * styling applied suitable only for the collection-deck. Do not use this
 * component anywhere outside of the collection-deck.
 */
export const Reel = forwardRef<ReelProps, typeof Stack>(
  (
    {
      as,
      minWidth = 'size-auto',
      maxWidth = 'size-auto',
      gap = 'spacing-none',
      className,
      snap,
      children,
      flexBasis = minWidth,
      shouldWrapChildren,
      sx,
    },
    reelRef
  ) => (
    <Stack
      ref={reelRef}
      as={as}
      direction="row"
      className={className}
      minWidth={minWidth}
      maxWidth={maxWidth}
      flexBasis={flexBasis}
      gap={gap}
      shouldWrapChildren={shouldWrapChildren}
      sx={{
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none' /* IE and Edge */,
        scrollbarWidth: 'none' /* Firefox */,
        overflowX: 'auto',
        ...(snap && {
          scrollBehavior: 'smooth',
          scrollSnapType: 'x mandatory',
        }),
        ...sx,
      }}
    >
      {React.Children.map(
        children,
        child =>
          React.isValidElement(child) &&
          React.cloneElement(child, {
            sx: {
              ...(snap && { scrollSnapAlign: 'start' }),
              ...(child?.props?.sx ?? {}),
            },
          })
      )}
    </Stack>
  )
)
